import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
import FormInput from '../../components/forms/FormInput';
import FormSelect from '../../components/forms/FormSelect';

import { getContracts, getExpiringSoon, createContract, updateContract, uploadContractDocument, deleteContract } from '../../api/contracts';
const contractTypes = [
  'Swimming Pool', 'Gym', 'Water Treatment Plant (WTP)', 'Sewage Treatment Plant (STP)',
  'Gas Refill', 'AVM Smart Kitchen, Gas Line', 'Kone Lift', 'CCC Zero Waste', 'Pollution',
  'Generator DG', 'DG Battery', 'Transformer', 'CCTV/Biometrics and Intercom',
  'Solar WattSun Energy System', 'Hybrid Hot Water Heater', 'Pumps in Water Tanks',
  'EV Charger', 'Invertor', 'Fire',
];

const tabs = [
  { key: '', label: 'All Contracts' },
  { key: 'active', label: 'Active' },
  { key: 'expiring_soon', label: 'Expiring Soon' },
  { key: 'expired', label: 'Expired' },
  { key: 'archived', label: 'Archived' },
];

const serviceEmojis = {
  security: '🔐',
  cleaning: '🧹',
  'water-treatment': '💧',
  'water treatment': '💧',
  garden: '🌿',
  'garden management': '🌿',
  'swimming pool': '🏊',
  pool: '🏊',
  pest: '🐛',
  elevator: '🛗',
  electrical: '⚡',
  plumbing: '🔧',
  fire: '🧯',
  intercom: '📞',
  generator: '⚙️',
  garbage: '🗑️',
};

const getServiceEmoji = (name) => {
  if (!name) return '📄';
  const lower = name.toLowerCase();
  for (const [key, emoji] of Object.entries(serviceEmojis)) {
    if (lower.includes(key)) return emoji;
  }
  return '📄';
};

const valueFrequencyOptions = [
  { value: 'total', label: 'Total' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const paymentFrequencyOptions = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half-yearly', label: 'Half-Yearly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one-time', label: 'One-Time' },
  { value: 'milestone', label: 'Milestone' },
];

export default function ContractsPage() {
  const [contracts, setContracts] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editFile, setEditFile] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteContractId, setDeleteContractId] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [showExpiringBanner, setShowExpiringBanner] = useState(true);
  const [showCriticalBanner, setShowCriticalBanner] = useState(true);
  const [filterService, setFilterService] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const navigate = useNavigate();
  const [form, setForm] = useState({
    custom_service_name: '', custom_type_manual: '', vendor_name: '', contact_person: '', contact_phone: '',
    start_date: '', end_date: '', contract_value: '',
    value_frequency: 'monthly', payment_frequency: 'monthly', document: null,
  });

  const fetchContracts = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (activeTab) params.status = activeTab;
      if (filterService) params.search = filterService;
      const res = await getContracts(params);
      setContracts(res.data || []);
      setMeta(res.meta || { current_page: 1, last_page: 1 });
    } catch (err) {
      console.error('Failed to load contracts', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filterService]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  useEffect(() => {
    getExpiringSoon().then((res) => setExpiringContracts(res.data?.data || [])).catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const resolvedType = form.custom_service_name === '__other__' ? form.custom_type_manual.trim() : form.custom_service_name;
    if (!resolvedType) return;
    setSubmitting(true);
    try {
      await createContract({ ...form, custom_service_name: resolvedType });
      setShowModal(false);
      setForm({
        custom_service_name: '', custom_type_manual: '', vendor_name: '', contact_person: '', contact_phone: '',
        start_date: '', end_date: '', contract_value: '',
        value_frequency: 'monthly', payment_frequency: 'monthly', document: null,
      });
      fetchContracts();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create contract');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (val, freq) => {
    const amount = `₹${Number(val || 0).toLocaleString('en-IN')}`;
    if (freq === 'monthly') return `${amount}/mo`;
    if (freq === 'quarterly') return `${amount}/qtr`;
    if (freq === 'yearly') return `${amount}/yr`;
    return amount;
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const openEditModal = (contract) => {
    setEditContract(contract);
    setEditFile(null);
    const existingType = contract.custom_service_name || '';
    const isCustomType = existingType && !contractTypes.includes(existingType);
    setEditForm({
      custom_service_name: isCustomType ? '__other__' : existingType,
      custom_type_manual: isCustomType ? existingType : '',
      vendor_name: contract.vendor?.name || '',
      contact_person: contract.vendor?.contact_person || '',
      contact_phone: contract.vendor?.phone || '',
      start_date: contract.start_date?.slice(0, 10) || '',
      end_date: contract.end_date?.slice(0, 10) || '',
      contract_value: contract.contract_value || '',
      value_frequency: contract.value_frequency || 'monthly',
      payment_frequency: contract.payment_frequency || 'monthly',
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const resolvedType = editForm.custom_service_name === '__other__' ? editForm.custom_type_manual.trim() : editForm.custom_service_name;
    if (!resolvedType) return;
    setEditSubmitting(true);
    try {
      await updateContract(editContract.id, { ...editForm, custom_service_name: resolvedType });
      if (editFile) {
        await uploadContractDocument(editContract.id, editFile);
      }
      setEditContract(null);
      setEditFile(null);
      fetchContracts();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update contract');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteContract(deleteContractId);
      setDeleteContractId(null);
      fetchContracts();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete contract');
    }
  };

  const criticalContract = expiringContracts.find((c) => getDaysRemaining(c.end_date) <= 7);

  const categoryOptions = [
    ...contractTypes.map((t) => ({ value: t, label: t })),
    { value: '__other__', label: 'Other (enter manually)' },
  ];

  return (
    <AppLayout title="Contracts (MOUs)" breadcrumbs="Contracts">
      {/* Sub Navigation */}
      <div
        className="flex overflow-x-auto -mx-8 -mt-8 mb-0"
        style={{
          background: 'white',
          borderBottom: '1px solid #E5E5E0',
          padding: '0 40px',
          gap: '32px',
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="whitespace-nowrap bg-transparent border-0 cursor-pointer transition-all"
            style={{
              padding: '16px 4px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: "'Lexend', sans-serif",
              color: activeTab === tab.key ? '#043959' : '#666666',
              borderBottom: `3px solid ${activeTab === tab.key ? '#043959' : 'transparent'}`,
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#043959';
                e.currentTarget.style.borderBottomColor = '#F4A460';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#666666';
                e.currentTarget.style.borderBottomColor = 'transparent';
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '32px 0' }}>

        {/* Expiring Contracts Banner */}
        {showExpiringBanner && expiringContracts.length > 0 && (
          <div
            className="flex items-start relative"
            style={{
              gap: '16px',
              padding: '20px 24px',
              borderRadius: '12px',
              marginBottom: '16px',
              borderLeft: '4px solid #F59E0B',
              background: 'rgba(254,243,199,0.5)',
            }}
          >
            <div className="flex-shrink-0 mt-0.5">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="9" x2="12" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <line x1="12" y1="17" x2="12.01" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>
                Contracts Expiring Soon
              </h4>
              <p className="text-sm" style={{ color: '#666666' }}>
                You have <strong style={{ color: '#1A1A1A' }}>{expiringContracts.length} contracts</strong> expiring in the next 30 days.{' '}
                <button
                  onClick={() => setActiveTab('expiring_soon')}
                  className="bg-transparent border-0 cursor-pointer font-medium hover:underline"
                  style={{ color: '#043959', fontFamily: "'Lexend', sans-serif", fontSize: '14px', padding: 0 }}
                >
                  View expiring contracts →
                </button>
              </p>
              <div className="flex flex-col" style={{ marginTop: '12px', gap: '2px' }}>
                {expiringContracts.slice(0, 5).map((contract) => (
                  <div key={contract.id} className="text-sm" style={{ padding: '4px 0', color: '#1A1A1A', borderBottom: '1px solid rgba(245,158,11,0.15)' }}>
                    {contract.custom_service_name || contract.service_category?.name || '-'}
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => setShowExpiringBanner(false)}
              className="flex items-center justify-center border-0 cursor-pointer bg-transparent"
              style={{ position: 'absolute', top: '14px', right: '16px', color: '#999', fontSize: '20px', lineHeight: 1, padding: 0 }}
              title="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        {/* Critical Expiry Alert */}
        {showCriticalBanner && criticalContract && (
          <div
            className="flex items-start relative"
            style={{
              gap: '16px',
              padding: '20px 24px',
              borderRadius: '12px',
              marginBottom: '24px',
              borderLeft: '4px solid #EF4444',
              background: 'rgba(254,226,226,0.4)',
            }}
          >
            <div className="flex-shrink-0 mt-0.5">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="3" fill="#EF4444"/>
                <path d="M9 12h6M12 9v6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>
                Urgent Action Required!
              </h4>
              <p className="text-sm" style={{ color: '#666666' }}>
                <strong style={{ color: '#1A1A1A' }}>
                  {criticalContract.custom_service_name || criticalContract.service_category?.name}
                </strong>{' '}
                contract expires in{' '}
                <strong style={{ color: '#1A1A1A' }}>
                  {getDaysRemaining(criticalContract.end_date)} days
                </strong>{' '}
                ({formatDate(criticalContract.end_date)}). Renew immediately to avoid service disruption.
              </p>
              <div className="flex gap-3 mt-3">
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-2 text-white text-xs font-medium rounded-lg transition-all hover:opacity-90 border-0 cursor-pointer"
                  style={{ padding: '6px 14px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
                >
                  Renew Now
                </button>
                <button
                  onClick={() => navigate('/quotations')}
                  className="inline-flex items-center gap-2 text-xs font-medium rounded-lg transition-all border cursor-pointer"
                  style={{
                    padding: '6px 12px',
                    background: '#FAFAF8',
                    color: '#1A1A1A',
                    borderColor: '#E5E5E0',
                    fontFamily: "'Lexend', sans-serif",
                  }}
                >
                  Get Quotations
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowCriticalBanner(false)}
              className="flex items-center justify-center border-0 cursor-pointer bg-transparent"
              style={{ position: 'absolute', top: '14px', right: '16px', color: '#999', fontSize: '20px', lineHeight: 1, padding: 0 }}
              title="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        {/* Card Header */}
        <div className="flex justify-between items-center" style={{ marginBottom: '20px' }}>
          <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Contract Management</h3>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 text-white text-sm font-medium rounded-lg transition-all hover:opacity-90 border-0 cursor-pointer"
            style={{
              padding: '10px 20px',
              background: '#043959',
              fontFamily: "'Lexend', sans-serif",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#032841';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#043959';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span>➕</span>
            New Contract
          </button>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-wrap" style={{ gap: '16px', marginBottom: '24px' }}>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium" style={{ color: '#666666' }}>Contract Type:</label>
            <select
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className="cursor-pointer"
              style={{
                padding: '8px 12px',
                border: '1px solid #E5E5E0',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: "'Lexend', sans-serif",
                background: 'white',
                outline: 'none',
              }}
            >
              <option value="">All Types</option>
              {contractTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium" style={{ color: '#666666' }}>Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setActiveTab(e.target.value);
              }}
              className="cursor-pointer"
              style={{
                padding: '8px 12px',
                border: '1px solid #E5E5E0',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: "'Lexend', sans-serif",
                background: 'white',
                outline: 'none',
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="expiring_soon">Expiring Soon</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium" style={{ color: '#666666' }}>Date Range:</label>
            <select
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="cursor-pointer"
              style={{
                padding: '8px 12px',
                border: '1px solid #E5E5E0',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: "'Lexend', sans-serif",
                background: 'white',
                outline: 'none',
              }}
            >
              <option value="">All Time</option>
              <option value="this_month">This Month</option>
              <option value="next_30">Next 30 Days</option>
              <option value="next_90">Next 90 Days</option>
            </select>
          </div>
        </div>

        {/* Contracts Table Card */}
        <div
          className="bg-white overflow-hidden"
          style={{
            borderRadius: '16px',
            border: '1px solid #E5E5E0',
            marginBottom: '24px',
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                  <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Contract Type</th>
                  <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Vendor Name</th>
                  <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Contact Person</th>
                  <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Contact Number</th>
                  <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Start Date</th>
                  <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>End Date</th>
                  <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Cost</th>

                  <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Status</th>
                  <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center text-sm" style={{ padding: '40px 16px', color: '#999999' }}>
                      Loading contracts...
                    </td>
                  </tr>
                ) : contracts.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center text-sm" style={{ padding: '40px 16px', color: '#999999' }}>
                      No contracts found
                    </td>
                  </tr>
                ) : (
                  contracts.map((contract) => {
                    const serviceName = contract.custom_service_name || contract.service_category?.name || '-';
                    const emoji = getServiceEmoji(serviceName);

                    return (
                      <tr
                        key={contract.id}
                        className="transition-colors"
                        style={{ borderBottom: '1px solid #F0F0EB' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#FAFAF8'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '16px', fontSize: '14px' }}>
                          {emoji} {serviceName}
                        </td>
                        <td style={{ padding: '16px', fontSize: '14px' }}>
                          {contract.vendor?.name || '-'}
                        </td>
                        <td style={{ padding: '16px', fontSize: '14px' }}>
                          {contract.vendor?.contact_person || '-'}
                        </td>
                        <td style={{ padding: '16px', fontSize: '14px' }}>
                          {contract.vendor?.phone || '-'}
                        </td>
                        <td style={{ padding: '16px', fontSize: '14px' }}>
                          {formatDate(contract.start_date)}
                        </td>
                        <td style={{ padding: '16px', fontSize: '14px' }}>
                          {formatDate(contract.end_date)}
                        </td>
                        <td style={{ padding: '16px', fontSize: '14px' }}>
                          {formatCurrency(contract.contract_value, contract.value_frequency)}
                        </td>
                        <td style={{ padding: '16px' }}>
                          <StatusBadge status={contract.status} />
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div className="flex gap-3 items-center">
                            {contract.document_path ? (
                              <button
                                onClick={() => window.open(`${(process.env.REACT_APP_API_URL || 'http://tgcaoa-backend.test/api').replace(/\/api$/, '')}/storage/${contract.document_path}`, '_blank')}
                                className="cursor-pointer transition-opacity hover:opacity-70"
                                title="View PDF"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                  <line x1="16" y1="13" x2="8" y2="13" />
                                  <line x1="16" y1="17" x2="8" y2="17" />
                                  <polyline points="10 9 9 9 8 9" />
                                </svg>
                              </button>
                            ) : (
                              <span style={{ width: 18 }} />
                            )}
                            <button
                              onClick={() => openEditModal(contract)}
                              className="cursor-pointer transition-opacity hover:opacity-70"
                              title="Edit"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setDeleteContractId(contract.id)}
                              className="cursor-pointer transition-opacity hover:opacity-70"
                              title="Delete"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6" />
                                <path d="M14 11v6" />
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {meta.last_page > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: meta.last_page }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => fetchContracts(page)}
                className="text-sm font-medium rounded-lg transition-all cursor-pointer"
                style={{
                  padding: '8px 14px',
                  background: page === meta.current_page ? '#043959' : 'white',
                  color: page === meta.current_page ? 'white' : '#1A1A1A',
                  border: `1px solid ${page === meta.current_page ? '#043959' : '#E5E5E0'}`,
                  fontFamily: "'Lexend', sans-serif",
                }}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteContractId} onClose={() => setDeleteContractId(null)} title="Delete Contract" size="sm">
        <div className="text-center py-2">
          <div className="flex items-center justify-center mb-4">
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </div>
          </div>
          <p className="text-sm mb-6" style={{ color: '#444', lineHeight: 1.6 }}>
            Are you sure you want to delete this contract?<br />
            <span style={{ color: '#999' }}>This action cannot be undone.</span>
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setDeleteContractId(null)}
              className="text-sm font-medium rounded-lg cursor-pointer"
              style={{ padding: '10px 28px', background: '#F5F5F5', color: '#1A1A1A', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="text-sm font-medium rounded-lg text-white cursor-pointer border-0"
              style={{ padding: '10px 28px', background: '#EF4444', fontFamily: "'Lexend', sans-serif" }}
            >
              Yes, Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Contract Modal */}
      <Modal isOpen={!!editContract} onClose={() => { setEditContract(null); setEditFile(null); }} title="Edit Contract" size="4xl">
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <FormSelect label="Contract Type *" name="custom_service_name" value={editForm.custom_service_name || ''} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} options={categoryOptions} required />
          {editForm.custom_service_name === '__other__' && (
            <FormInput label="Contract Type Name *" name="custom_type_manual" value={editForm.custom_type_manual || ''} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} placeholder="Enter contract type" required />
          )}
          <div className="grid grid-cols-3 gap-4">
            <FormInput label="Vendor Name *" name="vendor_name" value={editForm.vendor_name || ''} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} placeholder="Enter vendor name" required />
            <FormInput label="Contact Person" name="contact_person" value={editForm.contact_person || ''} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} placeholder="Contact person name" />
            <FormInput label="Contact Number" name="contact_phone" value={editForm.contact_phone || ''} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} placeholder="+91 xxxxx xxxxx" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Start Date *" name="start_date" type="date" value={editForm.start_date || ''} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} required />
            <FormInput label="End Date *" name="end_date" type="date" value={editForm.end_date || ''} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} required />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormInput label="Cost (₹) *" name="contract_value" type="number" value={editForm.contract_value || ''} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} placeholder="Enter amount" required />
            <FormSelect label="Value Frequency" name="value_frequency" value={editForm.value_frequency || 'monthly'} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} options={valueFrequencyOptions} />
            <FormSelect label="Payment Frequency" name="payment_frequency" value={editForm.payment_frequency || 'monthly'} onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })} options={paymentFrequencyOptions} />
          </div>
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>
              {editContract?.document_path ? 'Replace Contract PDF' : 'Attach Contract'}
            </label>
            {editContract?.document_path && (
              <div className="flex items-center gap-2 mb-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <button
                  type="button"
                  onClick={() => window.open(`${(process.env.REACT_APP_API_URL || 'http://tgcaoa-backend.test/api').replace(/\/api$/, '')}/storage/${editContract.document_path}`, '_blank')}
                  className="text-sm cursor-pointer bg-transparent border-0 hover:underline"
                  style={{ color: '#043959', fontFamily: "'Lexend', sans-serif", padding: 0 }}
                >
                  View current attachment
                </button>
              </div>
            )}
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setEditFile(e.target.files[0] || null)}
              className="w-full"
              style={{
                padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white',
              }}
            />
            <p style={{ fontSize: '11px', color: '#999999', marginTop: '4px' }}>
              {editContract?.document_path ? 'Upload a new PDF to replace the existing one. PDF only, max 10MB.' : 'PDF only, max 10MB'}
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid #E5E5E0' }}>
            <button
              type="button"
              onClick={() => { setEditContract(null); setEditFile(null); }}
              className="text-sm font-medium rounded-lg transition-all cursor-pointer"
              style={{ padding: '10px 20px', background: '#FAFAF8', color: '#1A1A1A', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={editSubmitting}
              className="text-sm font-medium rounded-lg text-white transition-all cursor-pointer border-0"
              style={{ padding: '10px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif", opacity: editSubmitting ? 0.7 : 1 }}
            >
              {editSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* New Contract Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="New Contract" size="4xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormSelect label="Contract Type *" name="custom_service_name" value={form.custom_service_name} onChange={handleChange} options={categoryOptions} required />
          {form.custom_service_name === '__other__' && (
            <FormInput label="Contract Type Name *" name="custom_type_manual" value={form.custom_type_manual} onChange={handleChange} placeholder="Enter contract type" required />
          )}
          <div className="grid grid-cols-3 gap-4">
            <FormInput label="Vendor Name *" name="vendor_name" value={form.vendor_name} onChange={handleChange} placeholder="Enter vendor name" required />
            <FormInput label="Contact Person" name="contact_person" value={form.contact_person} onChange={handleChange} placeholder="Contact person name" />
            <FormInput label="Contact Number" name="contact_phone" value={form.contact_phone} onChange={handleChange} placeholder="+91 xxxxx xxxxx" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Start Date *" name="start_date" type="date" value={form.start_date} onChange={handleChange} required />
            <FormInput label="End Date *" name="end_date" type="date" value={form.end_date} onChange={handleChange} required />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormInput label="Cost (₹) *" name="contract_value" type="number" value={form.contract_value} onChange={handleChange} placeholder="Enter amount" required />
            <FormSelect label="Value Frequency" name="value_frequency" value={form.value_frequency} onChange={handleChange} options={valueFrequencyOptions} />
            <FormSelect label="Payment Frequency" name="payment_frequency" value={form.payment_frequency} onChange={handleChange} options={paymentFrequencyOptions} />
          </div>

          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Attach Contract</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setForm({ ...form, document: e.target.files[0] || null })}
              className="w-full"
              style={{
                padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white',
              }}
            />
            <p style={{ fontSize: '11px', color: '#999999', marginTop: '4px' }}>PDF only, max 10MB</p>
          </div>
          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid #E5E5E0' }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="text-sm font-medium rounded-lg transition-all cursor-pointer"
              style={{
                padding: '10px 20px',
                background: '#FAFAF8',
                color: '#1A1A1A',
                border: '1px solid #E5E5E0',
                fontFamily: "'Lexend', sans-serif",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="text-sm font-medium rounded-lg text-white transition-all cursor-pointer border-0"
              style={{
                padding: '10px 20px',
                background: '#043959',
                fontFamily: "'Lexend', sans-serif",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Creating...' : 'Create Contract'}
            </button>
          </div>
        </form>
      </Modal>
    </AppLayout>
  );
}
